// © Microsoft Corporation. All rights reserved.
// Provides utility functions to get and set data from browser webStorage.
import { tryParseJson } from "../utilities/Parsing";
import { logger } from "@msnews/diagnostics";
/**
 * Base class for localeStorage and sessionStorage abstractions
 */
export class WebStorage {
    /**
     * Check if the web storage is supported
     * @returns {boolean} true if supported
     */
    get supported() {
        return !!this.storage;
    }
    /**
     * Local storage object for persistent storage
     * @param storage Storage used.
     */
    constructor(storage) {
        this.storage = storage;
    }
    /**
     * Get given key's value from web storage
     * @param {string} key - Key name
     * @returns {string} Key value
     */
    getItem(key) {
        if (this.supported) {
            return this.storage.getItem(key);
        }
    }
    /**
     * Gets the value from local storage using the key.
     * @param {string} key - the storage key to store under
     * @param {T} defaultValue - the default value to return if parsing fails
     * @returns {T} The parsed JSON value
     */
    getObject(key, defaultValue) {
        const value = this.getItem(key);
        if (value != null) {
            const parsedObj = tryParseJson(value);
            if (parsedObj != null) {
                return parsedObj;
            }
        }
        return defaultValue;
    }
    /**
     * Gets given index location key from web storage
     * @param {string} key - Key name
     * @returns {string} Key value
     */
    key(index) {
        if (this.supported && index >= 0) {
            return this.storage.key(index);
        }
    }
    /**
     * Returns all the keys under the storage
     */
    keys() {
        return this.supported
            ? Object.keys(this.storage)
            : [];
    }
    /**
     * Sets the given value after stringifying as JSON
     * @param {string} key - the storage key to store under
     * @param {T} value - the value to serialize and store
     */
    setObject(key, value) {
        (value !== undefined)
            ? this.setItem(key, JSON.stringify(value))
            : this.removeItem(key);
    }
    /**
     * Removes the given key from storage and returns the existing value parsed
     * @param {string} key - the key to remove.
     */
    removeObject(key) {
        const value = this.removeItem(key);
        if (value != null) {
            return tryParseJson(value);
        }
    }
    /**
     * Set given key's value to local storage
     * @param {string} key - Key name
     * @param {string} value - Key value
     * @param {boolean} ignoreError - log error instead of throwing
     */
    setItem(key, value, ignoreError = true) {
        if (this.supported) {
            try {
                if (!value) {
                    throw "Attempted to store null/undefined value: " + value;
                }
                this.storage.setItem(key, value);
            }
            catch (e) {
                if (ignoreError) {
                    logger.logError(e);
                }
                else {
                    throw e;
                }
            }
        }
        else if (!ignoreError) {
            throw new Error("WebStorage not supported");
        }
    }
    /**
     * Get entry count of storage
     * @returns storage.length
     */
    get length() {
        if (this.supported) {
            return this.storage.length;
        }
    }
    /**
     * Remove key item from local storage
     * @param {string} key - Key name
     * @returns {string} Key value
     */
    removeItem(key) {
        if (this.supported) {
            const value = this.getItem(key);
            this.storage.removeItem(key);
            return value;
        }
    }
    /**
     * Removes all key/value pairs from storage, if there are any
     */
    clear() {
        if (this.supported) {
            this.storage.clear();
        }
    }
    /**
     * Removes all keys containing the given substring in the storage key
     * @param {string} substring The substring to check
     */
    removeSubstringKeys(substring) {
        if (!this.supported || !substring) {
            return;
        }
        const matchingKeys = [];
        for (let index = 0; index < this.storage.length; index++) {
            const storageKey = this.key(index);
            if (storageKey && storageKey.includes(substring)) {
                matchingKeys.push(storageKey);
            }
        }
        for (let index = 0; index < matchingKeys.length; index++) {
            this.removeItem(matchingKeys[index]);
        }
    }
}
//# sourceMappingURL=WebStorage.js.map